// Kennesaw State University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - KSU's Common Data Set 2025-2026, published as a web page by the Office
//    of Data Strategy (no PDF; KSU says to email for one): C1 applicants,
//    admits and enrollees for Fall 2025, C8 test policy for Fall 2027,
//    C21/C22 plans, G1/G5 2026-2027 costs. The C1 residency table is blank.
//  - KSU Undergraduate Admissions: Freshman and Transfer Freshman Application
//    Steps (deadlines table for Fall 2027, testing requirements and FAQs,
//    Architecture requirements), checked against the Fall 2027 First-Year
//    Applicant Timeline. Neither page publishes decision dates.
//  - College Scorecard, school id 486840 (net price by family income). The
//    shared DEMO_KEY was over its rate limit, so the figures were read from
//    the Scorecard school page's own API response for KSU.
// Cost: the CDS G1/G5 items are used. The financial aid office's 2026-2027
// cost page lists the same items plus about $65 in loan fees, and its
// out-of-state total ($44,977) is $1 below the sum of its own items.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'KSU Common Data Set 2025-2026',
        url: 'https://campus.kennesaw.edu/offices-services/data-strategy/institutional-research/publications/common-data-set/cds-2025-2026.php',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'KSU Admissions, Freshman and Transfer Freshman Application Steps',
        url: 'https://www.kennesaw.edu/admissions/undergraduate/admission-requirements/freshman.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?486840-Kennesaw-State-University',
        read: '2026-10-15',
    },
    coa: {
        label: 'KSU Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://campus.kennesaw.edu/current-students/financial-aid/student-resources/cost-of-attendance.php',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'kennesaw-state-university',
    kbId: 'kennesaw_state_university',
    name: 'Kennesaw State University',
    shortName: 'Kennesaw State',
    aliases: ['KSU'],
    location: 'Kennesaw, GA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (KSU color guidelines); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#FFC629',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/kennesaw_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 33513,
        admitted: 26622,
        enrolled: 9537,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'KSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'KSU has no early decision or early action plan. Its November 2 priority deadline is an earlier filing date, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to KSU, not to a college. One program asks for more:',
        rows: [
            { school: 'Architecture', requirement: 'Higher requirements and limited seats: a 3.0 GPA, an SAT math score of 570 or ACT math score of 23 (CLT math 22), and KSU encourages you to finish your application and documents by January 31.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'July 1' },
        rows: [
            { plan: 'Priority deadline', deadline: 'November 2', decision: null, binding: false },
            { plan: 'Final deadline', deadline: 'July 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1. For the priority deadline, documents are due November 16; applying by then gives you early access to on-campus housing and orientation sign-ups. For the final deadline, documents are also due July 1. Classes begin August 23, 2027. KSU does not publish decision dates.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'KSU is not test-optional: every first-year applicant must send official SAT or ACT scores, and starting fall 2027 it also accepts the CLT. For fall 2027 you need at least a 3.0 high school GPA and minimum section scores: SAT 500 reading and writing and 490 math, or ACT 18 English or reading and 18 math. KSU superscores within the SAT and within the ACT, not across them.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by July 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (15 hours)', amounts: [5760, 21660] },
            { item: 'Fees', amounts: [1188, 1188] },
            { item: 'Housing (on campus)', amounts: [9332, 9332] },
            { item: 'Food', amounts: [5016, 5016] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1500, 1500] },
            { item: 'Transportation', amounts: [2222, 2222] },
            { item: 'Personal expenses', amounts: [3995, 3995] },
            { item: 'Loan fees', amounts: [64, 65] },
        ],
        totals: [29077, 44978],
        note: 'Undergraduates on campus. KSU prints the out-of-state total as $44,977; its items add to $44,978, the figure shown here.',
        source: 'coa',
        netPrice: {
            average: 16325,
            byIncome: [
                { income: '$0 to $30,000', amount: 14182 },
                { income: '$30,001 to $48,000', amount: 14246 },
                { income: '$48,001 to $75,000', amount: 15409 },
                { income: '$75,001 to $110,000', amount: 18832 },
                { income: 'Over $110,000', amount: 21295 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from KSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
